import { ErrorLevel, getLoggableError, LoggableError } from '@griply/utils';
import * as Sentry from '@sentry/react';
import { ID } from 'shared/types/id';

export type CaptureContext = Partial<LoggableError>;

enum SentryLevel {
  Fatal = 'fatal',
  Error = 'error',
  Warning = 'warning',
  Log = 'log',
  Info = 'info',
  Debug = 'debug',
}

const toSentryLevel = (level: ErrorLevel): SentryLevel => {
  if (level === ErrorLevel.Fatal) {
    return SentryLevel.Fatal;
  }
  if (level === ErrorLevel.Warning) {
    return SentryLevel.Warning;
  }
  if (level === ErrorLevel.Log) {
    return SentryLevel.Log;
  }
  if (level === ErrorLevel.Info) {
    return SentryLevel.Info;
  }
  if (level === ErrorLevel.Debug) {
    return SentryLevel.Debug;
  }
  return SentryLevel.Error;
};

const filterDefined = <T extends Record<string, unknown>>(record: T): T => {
  const filtered = {} as Record<string, unknown>;
  Object.entries(record).forEach(([key, value]) => {
    if (value !== undefined) {
      filtered[key] = value;
    }
  });
  return filtered as T;
};

const applyScope = (scope: Sentry.Scope, loggable: LoggableError) => {
  scope.setTags(filterDefined(loggable.tags));
  scope.setExtras(loggable.extra);
  scope.setLevel(toSentryLevel(loggable.level));
  Object.entries(loggable.contexts).forEach(([key, value]) => {
    scope.setContext(key, value);
  });
  if (loggable.fingerprint) {
    scope.setFingerprint(loggable.fingerprint);
  }
};

const mergeLoggable = (
  base: LoggableError,
  override: CaptureContext,
): LoggableError => ({
  tags: { ...base.tags, ...(override.tags ?? {}) },
  contexts: { ...base.contexts, ...(override.contexts ?? {}) },
  extra: { ...base.extra, ...(override.extra ?? {}) },
  level: override.level ?? base.level,
  fingerprint: override.fingerprint ?? base.fingerprint,
});

const getDefaultContext = () => {
  if (typeof window === 'undefined') {
    return {};
  }
  return { url: window.location.href, platform: 'web' };
};

export type ErrorReporter = {
  captureException: (
    error: unknown,
    context?: CaptureContext,
  ) => string | undefined;
  captureMessage: (
    message: string,
    level?: ErrorLevel,
    context?: CaptureContext,
  ) => string | undefined;
  setUser: (user: { id: ID } | null) => void;
  reset: () => void;
  addBreadcrumb: (breadcrumb: {
    category?: string;
    message: string;
    level?: ErrorLevel;
    data?: Record<string, unknown>;
  }) => void;
  increment: (
    name: string,
    value?: number,
    tags?: Record<string, string>,
  ) => void;
};

let reporter: ErrorReporter | undefined;

const buildReporter = (): ErrorReporter => ({
  captureException: (error, context = {}) => {
    const base = getLoggableError(error, getDefaultContext());
    const merged = mergeLoggable({ ...base, level: ErrorLevel.Error }, context);
    return Sentry.withScope((scope) => {
      applyScope(scope, merged);
      return Sentry.captureException(error);
    });
  },
  captureMessage: (message, level = ErrorLevel.Info, context = {}) => {
    const base = getLoggableError(null, { ...getDefaultContext(), level });
    const merged = mergeLoggable(base, context);
    return Sentry.withScope((scope) => {
      applyScope(scope, merged);
      return Sentry.captureMessage(message, toSentryLevel(merged.level));
    });
  },
  setUser: (user) => {
    if (!user) {
      Sentry.setUser(null);
      return;
    }
    Sentry.setUser({ id: user.id });
  },
  reset: () => {
    Sentry.setUser(null);
    Sentry.getCurrentScope().clear();
  },
  addBreadcrumb: (breadcrumb) => {
    Sentry.addBreadcrumb({
      category: breadcrumb.category,
      message: breadcrumb.message,
      level: breadcrumb.level
        ? toSentryLevel(breadcrumb.level)
        : SentryLevel.Info,
      data: breadcrumb.data,
    });
  },
  increment: (name, value = 1, tags = {}) => {
    try {
      Sentry.addBreadcrumb({
        category: 'metric',
        message: name,
        level: SentryLevel.Info,
        data: { value, ...tags },
      });
      Sentry.logger.info(`metric.${name}`, { value, ...tags });
    } catch {
      // telemetry must never crash the app
    }
  },
});

export const getErrorReporter = (): ErrorReporter => {
  if (!reporter) {
    reporter = buildReporter();
  }
  return reporter;
};

export const __resetErrorReporterForTests = () => {
  reporter = undefined;
};
